import { ref, readonly } from 'vue';
import * as CookieConsent from 'vanilla-cookieconsent';
import type { CookieConsentConfig } from 'vanilla-cookieconsent';

// Reactive state (module-level singleton)
const acceptedAnalytics = ref(false);
const acceptedMarketing = ref(false);
let initialized = false;

const IGNORED_PATHS = ['/privacy-policy'];

function syncState() {
    acceptedAnalytics.value = CookieConsent.acceptedCategory('analytics');
    acceptedMarketing.value = CookieConsent.acceptedCategory('marketing');
}

function fireGtmConsentUpdate() {
    if (typeof window === 'undefined' || !window.dataLayer) return;

    const analytics = CookieConsent.acceptedCategory('analytics');
    const marketing = CookieConsent.acceptedCategory('marketing');

    // Google Consent Mode v2
    window.gtag?.('consent', 'update', {
        analytics_storage: analytics ? 'granted' : 'denied',
        ad_storage: marketing ? 'granted' : 'denied',
        ad_user_data: marketing ? 'granted' : 'denied',
        ad_personalization: marketing ? 'granted' : 'denied',
    });

    // Legacy GTM event for existing triggers
    window.dataLayer.push({ event: 'cookie_refresh' });
}

const config: CookieConsentConfig = {
    cookie: {
        name: 'cc_consent',
        expiresAfterDays: 365,
    },
    guiOptions: {
        consentModal: { layout: 'box inline', position: 'bottom right' },
        preferencesModal: { layout: 'box' },
    },
    categories: {
        necessary: { enabled: true, readOnly: true },
        analytics: {
            enabled: false,
            autoClear: { cookies: [{ name: /^_ga/ }, { name: /^_gid/ }] },
        },
        marketing: {
            enabled: false,
            autoClear: { cookies: [{ name: /^_fb/ }, { name: /^_gcl/ }] },
        },
    },
    language: {
        default: 'en',
        translations: {
            en: {
                consentModal: {
                    title: 'Cookie Notice',
                    description: 'This site uses various technologies, including third-party cookies, to personalize website functionalities, measure usage and performance, and provide targeted advertising. By continuing to use this website you consent to our Privacy Policy and Terms of Use.',
                    acceptAllBtn: 'Accept Cookies',
                    acceptNecessaryBtn: 'Reject Cookies',
                    showPreferencesBtn: 'Manage Preferences',
                    footer: '<a href="/privacy-policy">More Information</a>',
                },
                preferencesModal: {
                    title: 'Cookie Preferences',
                    acceptAllBtn: 'Accept All',
                    acceptNecessaryBtn: 'Reject All',
                    savePreferencesBtn: 'Save Preferences',
                    sections: [
                        {
                            title: 'Cookie Usage',
                            description: 'We use cookies to ensure basic website functionality and to enhance your experience. Choose for each category to opt-in/out.',
                        },
                        {
                            title: 'Essential Cookies',
                            description: 'Essential cookies ensure the website functions properly and that your preferences are saved.',
                            linkedCategory: 'necessary',
                        },
                        {
                            title: 'Analytical Cookies',
                            description: 'Allow us to analyse website use and improve the visitor experience.',
                            linkedCategory: 'analytics',
                        },
                        {
                            title: 'Marketing Cookies',
                            description: 'Allow us to personalise your experience and send you relevant content and offers.',
                            linkedCategory: 'marketing',
                        },
                    ],
                },
            },
        },
    },
    onFirstConsent: () => {
        syncState();
        fireGtmConsentUpdate();
        // Clean up old cookie from previous system
        document.cookie = '__cookie_consent=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    },
    onConsent: () => {
        syncState();
        fireGtmConsentUpdate();
    },
    onChange: () => {
        syncState();
        fireGtmConsentUpdate();
    },
};

export function initCookieConsent(): void {
    if (initialized || typeof window === 'undefined') return;

    if (IGNORED_PATHS.includes(window.location.pathname)) return;

    CookieConsent.run(config);
    syncState();
    initialized = true;
}

export default function useCookieConsent() {
    return {
        acceptedAnalytics: readonly(acceptedAnalytics),
        acceptedMarketing: readonly(acceptedMarketing),
        showPreferences: () => CookieConsent.showPreferences(),
        show: () => CookieConsent.show(),
        hide: () => CookieConsent.hide(),
        acceptedCategory: (cat: string) => CookieConsent.acceptedCategory(cat),
    };
}
